import { Autocomplete, TextField, Theme, Box, FormControlLabel, Switch } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, FC, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { Page, PageTitle, Button, PageTitleWithButton } from '../../components';
import { UserContext, VendorPageContext } from '../../context';
import { vendorLinks, getLoggedInUserPartnerTypes } from '../../helpers';
import { TodosList } from '../production/punchlists/todos/TodosList';
import { ISelectableVendor, INavLink } from '../../models';

interface IVendorPage {
  children?: any;
  isPunchlistPage?: boolean;
}

export const VendorPage: FC<IVendorPage> = ({ children, isPunchlistPage }) => {
  const classes = useStyles();
  const { user, isAdmin } = useContext(UserContext);
  const { handleVendorSelect, vendorName, loadingVendors, vendors } = useContext(VendorPageContext);
  const [isTodoModalOpen, showTodoModal] = useState(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const { path } = useRouteMatch();
  // get the logged in user name if there is one
  const name: string | null =
    user?.externalUser?.firstName && user?.externalUser?.lastName
      ? `${user.externalUser.firstName} ${user.externalUser.lastName}`
      : null;
  // find the link name based on the route;
  const selectedVendorLink: INavLink | null =
    vendorLinks.find(link => link.url === path) ??
    vendorLinks
      .filter(link => !!link.links)
      .map(link => {
        return link.links.find(sublink => sublink.url === path);
      })[0];

  // set the pageTitle to the property name of the selected route otherwise fallback to the potal name
  const pageTitle = selectedVendorLink?.name ?? 'Vendors';
  // add the page title and logged in name
  const title: string = `${pageTitle}${name ? ` - ${name}` : ''}`;
  return (
    <Page title={pageTitle}>
      {path === '/vendors' ? (
        <PageTitle showDivider title={title} />
      ) : (
        <PageTitleWithButton
          pageTitleBackProps={{
            showDivider: false,
            title,
            backText: 'Back to Vendors Portal',
            backUrl: '/vendors'
          }}
          button={
            <Box
              display="flex"
              flexDirection="column"
              alignItems={{ xs: 'center', sm: 'flex-end' }}
            >
              {isPunchlistPage && (
                <FormControlLabel
                  className="print--none"
                  sx={{
                    marginBottom: '0.5rem',
                    marginRight: 0
                  }}
                  control={
                    <Switch
                      color="primary"
                      checked={showArchived}
                      id="archived-active-vendor"
                      disabled={loadingVendors}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setShowArchived(e.target.checked);
                      }}
                      name={'archived-active-vendor'}
                    />
                  }
                  label={'Include Archived'}
                />
              )}
              <Box display="flex" width={{ xs: '100%', sm: 'auto' }}>
                <>
                  {/* admin user or the current user has more than one vendor */}
                  {(isAdmin || getLoggedInUserPartnerTypes(user).length > 1) && (
                    <>
                      <Autocomplete
                        value={loadingVendors ? 'Loading...' : vendorName ? vendorName : 'All'}
                        onChange={(event, newValue: any) => {
                          handleVendorSelect(newValue);
                        }}
                        disabled={loadingVendors}
                        selectOnFocus
                        handleHomeEndKeys
                        classes={{
                          root: classes.autocomplete
                        }}
                        loading={loadingVendors}
                        id="selected-vendor"
                        options={vendors || []}
                        getOptionLabel={(option: ISelectableVendor) => {
                          // Value selected with enter, right from the input
                          if (typeof option === 'string') {
                            return option;
                          }
                          return `${
                            option.name
                              ? `${option.name}${
                                  option?.vendorSageId ? ` - ${option?.vendorSageId}` : ''
                                }`
                              : ''
                          }`;
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            InputLabelProps={{
                              className: classes.ellipsis
                            }}
                            key={params.id}
                            size="small"
                            autoComplete="on"
                            label="Vendor"
                            variant="outlined"
                          />
                        )}
                      />
                    </>
                  )}
                  {isPunchlistPage && isAdmin && (
                    <Button
                      id="add-todo"
                      onClick={() => {
                        showTodoModal(true);
                      }}
                      className={classes.addTodoButton}
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                    >
                      Add Todo
                    </Button>
                  )}
                </>
              </Box>
            </Box>
          }
        />
      )}
      {isPunchlistPage && !children && (
        <TodosList
          route="vendors"
          isTodoModalOpen={isTodoModalOpen}
          showTodoModal={showTodoModal}
          showArchived={showArchived}
          excludeEmptyForVendors={true}
        />
      )}
      {children && !isPunchlistPage && <>{children}</>}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    justifyContent: 'space-around',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  autocomplete: {
    width: '50%',
    '@media (min-width: 600px)': {
      width: '20rem',
      marginRight: theme.spacing(1)
    }
  },
  addTodoButton: {
    width: '50%',
    '@media (min-width: 600px)': {
      width: 'auto'
    }
  },
  link: {
    textDecoration: 'none'
  },
  infoWrapper: {
    align: 'center',
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  pageContainer: {
    justifyContent: 'space-around',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'normal'
    }
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
